import React, {
  useEffect,
  useState,
  useCallback,
  useContext,
  useMemo,
  useRef,
} from "react";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import interactionPlugin from "@fullcalendar/interaction";
import {
  Container,
  Modal,
  Button,
  Form,
  Col,
  Row,
  Badge,
} from "react-bootstrap";
import { useForm } from "react-hook-form";
import {
  CALENDAR_EVENTS_URL,
  CALENDAR_URL,
  APPOINTMENT_TYPES_URL,
} from "../../../api/Urls";
import { api } from "../../../api/Middleware";
import ptBrLocale from "@fullcalendar/core/locales/pt-br";
import MoonLoader from "react-spinners/MoonLoader";
import { Controller } from "react-hook-form";
import NumberFormat from "react-number-format";
import Swal from "sweetalert2";
import {
  format,
  startOfMonth,
  endOfMonth,
  eachDayOfInterval,
  isSameDay,
  isToday,
} from "date-fns";
import { ptBR } from "date-fns/locale";
import {
  IconCircleFilled,
  IconEdit,
  IconChevronDown,
  IconArchive,
  IconInfoCircleFilled,
  IconCircleCheckFilled,
  IconCircleXFilled,
  IconUserExclamation,
} from "@tabler/icons-react";
import "./calendar.css";
import "./calendarMobile.css";
import "./dayView.css";
import UserContext from "../../../contexts/UserContext";
import { FEATURE_IDS } from "../../../constants/FeatureIds";

import EventDetailsModal from "./EventDetailsModal";
import DayEventsModal from "./DayEventsModal";
import { formatCurrency } from "./utils";
import {  formatPhoneNumber } from "../../../helpers/Helpers";
import ClientSelector from "./components/ClientSelector";
import ClientSearch from "./components/ClientSearch";
import ArchiveCalendarModal from "./components/ArchiveCalendarModal";
import ArchivedCalendarsModal from "./components/ArchivedCalendarsModal";
import TagsSelector from "../Tags/TagsSelector";

const CALENDAR_COLORS = [
  "#3498db", // Azul claro
  "#2ecc71", // Verde esmeralda
  "#e74c3c", // Vermelho pomegranate
  "#9b59b6", // Roxo ametista
  "#f1c40f", // Amarelo sol
  "#1abc9c", // Verde turquesa
  "#e67e22", // Laranja cenoura
  "#34495e", // Azul escuro
  "#d35400", // Laranja escuro
  "#8e44ad", // Roxo escuro
];

// Legenda visível dos status dos agendamentos.
// Espelha os 4 estados que aparecem como ícone em cada evento do calendário.
// "missing" é um STATUS de CalendarEvents; o resto vem de CONFIRMATION_CHOICES.
const EVENT_LEGEND_STATUS = {
  pending: { label: "Pendente", color: "#ff9900", Icon: IconInfoCircleFilled },
  confirmed: { label: "Confirmado", color: "#28a745", Icon: IconCircleCheckFilled },
  canceled: { label: "Cancelado", color: "#dc3545", Icon: IconCircleXFilled },
  missing: { label: "Faltou", color: "#6c757d", Icon: IconUserExclamation },
};

const Calendar = () => {
  const { user, bottyData, settings, hasPermission } = useContext(UserContext);

  // Permissões granulares da Agenda (feature ID 7)
  const canCreate = hasPermission(FEATURE_IDS.AGENDA, "create");
  const canUpdate = hasPermission(FEATURE_IDS.AGENDA, "update");
  const canDelete = hasPermission(FEATURE_IDS.AGENDA, "delete");

  const [calendars, setCalendars] = useState([]);
  const [events, setEvents] = useState([]);
  const [appointmentTypes, setAppointmentTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [selectedInfo, setSelectedInfo] = useState(null);
  const [showCalendarModal, setShowCalendarModal] = useState(false);
  const [showEditCalendarModal, setShowEditCalendarModal] = useState(false);
  const [editingCalendar, setEditingCalendar] = useState(null);
  // Automações disparadas em agendamento manual (seleção por agenda)
  const [manualReminderOptions, setManualReminderOptions] = useState([]);
  const [selectedReminderKeys, setSelectedReminderKeys] = useState([]);
  const [loadingManualReminders, setLoadingManualReminders] = useState(false);
  const [showArchiveModal, setShowArchiveModal] = useState(false);
  const [showArchivedListModal, setShowArchivedListModal] = useState(false);
  const [archivingCalendar, setArchivingCalendar] = useState(null);
  const [selectedCalendars, setSelectedCalendars] = useState([]);
  const [showEventModal, setShowEventModal] = useState(false);
  const [calendarColors, setCalendarColors] = useState({});
  const [showEventDetailsModal, setShowEventDetailsModal] = useState(false);
  const [selectedEventForDetails, setSelectedEventForDetails] = useState(null);
  const [showDayEventsModal, setShowDayEventsModal] = useState(false);
  const [selectedDayForEvents, setSelectedDayForEvents] = useState(null);
  const [currentDateRange, setCurrentDateRange] = useState({
    start: null,
    end: null,
  });

  // Estados para o mini calendário
  const [currentDate, setCurrentDate] = useState(new Date());
  const [miniCalendarDate, setMiniCalendarDate] = useState(new Date());
  const [fullCalendarRef, setFullCalendarRef] = useState(null);

  const [selectedClient, setSelectedClient] = useState(null);

  // Tipos de atendimento agrupados por agenda
  const [expandedCalendars, setExpandedCalendars] = useState(new Set());
  const typesByCalendar = useMemo(() => {
    const map = {};
    appointmentTypes.forEach((t) => {
      const calId = t.calendar?.id ?? '__none__';
      if (!map[calId]) map[calId] = [];
      map[calId].push(t);
    });
    return map;
  }, [appointmentTypes]);

  const toggleCalendarTypes = (calId) => {
    setExpandedCalendars((prev) => {
      const next = new Set(prev);
      next.has(calId) ? next.delete(calId) : next.add(calId);
      return next;
    });
  };

  // Estados para mobile/tablet
  const [showBottomSheet, setShowBottomSheet] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [isTablet, setIsTablet] = useState(
    window.innerWidth > 768 && window.innerWidth <= 1024
  );

  // *** NOVOS Estados para long press mais robustos ***
  const [longPressState, setLongPressState] = useState({
    isActive: false,
    timer: null,
    startTime: 0,
    startPosition: { x: 0, y: 0 },
    currentPosition: { x: 0, y: 0 },
    targetElement: null,
    dateInfo: null,
    hasMoved: false,
    scrollDetected: false,
  });

  // Refs para controle mais preciso
  const longPressRef = useRef(longPressState);
  const touchInterruptRef = useRef(false);
  const isLongPressActiveRef = useRef(false);

  // Configurações
  const [vibrationEnabled, setVibrationEnabled] = useState(true);
  // const [sendAutomations, setSendAutomations] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    control,
    watch,
    clearErrors,
    formState: { errors },
  } = useForm();

  const {
    register: registerCalendar,
    handleSubmit: handleSubmitCalendar,
    reset: resetCalendar,
  } = useForm();

  const {
    register: registerEditCalendar,
    handleSubmit: handleSubmitEditCalendar,
    reset: resetEditCalendar,
    setValue: setEditCalendarValue,
  } = useForm();

  // Observar o tipo de atendimento selecionado
  const selectedAppointmentType = watch("appointment_type");
  const selectedCalendar = watch("calendar");
  const watchedSendAutomations = watch("send_automations");

  // Tipos filtrados pela agenda selecionada no formulário de evento
  const filteredAppointmentTypes = useMemo(() => {
    if (!selectedCalendar) return [];
    const calendarId = parseInt(selectedCalendar);
    return appointmentTypes.filter((t) => t.calendar?.id === calendarId);
  }, [appointmentTypes, selectedCalendar]);

  // Ref para detectar mudanças reais na agenda selecionada (e limpar o tipo)
  const prevCalendarRef = useRef(null);

  const handleClientSelected = (client) => {
    setSelectedClient(client);
    
    if (client) {
      // Configurar título no formato {Nome} - {Telefone}
      const clientTitle = `${client.full_name} - ${formatPhoneNumber(client.phone_primary)}`;
      setValue("title", clientTitle);
      
      // Configurar descrição no formato solicitado
      const clientDescription = `Cliente: ${client.full_name}\nTelefone: ${formatPhoneNumber(client.phone_primary)}`;
      setValue("description", clientDescription);
      
      // Preencher campos de automação
      setValue("client_name", client.full_name);
      setValue("client_phone", formatPhoneNumber(client.phone_primary));
      
      // Limpar erros se houver
      clearErrors(["client_name", "client_phone"]);
    } else {
      // Limpar campos quando cliente é removido
      setValue("title", "");
      setValue("description", "");
      setValue("client_name", "");
      setValue("client_phone", "");
    }
  };

  // Função debounce para performance
  function debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }

  // Detectar mudanças de tamanho de tela
  useEffect(() => {
    const checkScreenSize = () => {
      const width = window.innerWidth;
      setIsMobile(width <= 768);
      setIsTablet(width > 768 && width <= 1024);

      if (fullCalendarRef) {
        setTimeout(() => {
          fullCalendarRef.updateSize();
        }, 100);
      }
    };

    const debouncedCheck = debounce(checkScreenSize, 100);
    window.addEventListener("resize", debouncedCheck);
    window.addEventListener("orientationchange", debouncedCheck);

    return () => {
      window.removeEventListener("resize", debouncedCheck);
      window.removeEventListener("orientationchange", debouncedCheck);
    };
  }, [fullCalendarRef]);

  const calculateSlotDuration = (settings) => {
    const appointmentDuration =
      settings.current?.business_hours_settings?.appointment_duration || 30;
    const appointmentInterval =
      settings.current?.business_hours_settings?.appointment_interval || 0;

    // Calcular o total de minutos por slot (duração + intervalo)
    const totalMinutes = appointmentDuration + appointmentInterval;

    // Garantir que o valor seja pelo menos 1 minuto
    const finalMinutes = Math.max(totalMinutes, 1);

    // Converter para horas e minutos
    const hours = Math.floor(finalMinutes / 60);
    const minutes = finalMinutes % 60;

    // Formatar para o padrão HH:MM:SS exigido pelo FullCalendar
    const formattedHours = hours.toString().padStart(2, "0");
    const formattedMinutes = minutes.toString().padStart(2, "0");

    return `${formattedHours}:${formattedMinutes}:00`;
  };

  // Função para calcular os dias ocultos
  const calendarSettings = useMemo(() => {
    const apiWorkingDays =
      settings.current?.business_hours_settings?.working_days || [];

    // Função para formatar horário para o padrão FullCalendar (HH:MM)
    const formatTimeForFullCalendar = (timeString) => {
      if (!timeString) return null;

      // Se já está no formato HH:MM, retornar como está
      if (timeString.length === 5 && timeString.includes(":")) {
        return timeString;
      }

      // Se está no formato HH:MM:SS, remover os segundos
      if (timeString.length === 8 && timeString.includes(":")) {
        return timeString.substring(0, 5);
      }

      return null;
    };

    // Definir horários com valores padrão seguros
    let startTime = "06:00"; // Padrão seguro
    let endTime = "23:00"; // Padrão seguro

    // Para usuários level 0, usar horários da API se disponíveis
    if (user?.superuser_level === 0) {
      const apiStartTime =
        settings.current?.business_hours_settings?.start_time;
      const apiEndTime = settings.current?.business_hours_settings?.end_time;

      if (apiStartTime) {
        const formattedStart = formatTimeForFullCalendar(apiStartTime);
        if (formattedStart) startTime = formattedStart;
      }

      if (apiEndTime) {
        const formattedEnd = formatTimeForFullCalendar(apiEndTime);
        if (formattedEnd) endTime = formattedEnd;
      }
    }

    // Se não há configuração de dias OU usuário não é level 0, mostrar todos os dias
    if (apiWorkingDays.length === 0 || user?.superuser_level !== 0) {
      return {
        hiddenDays: [],
        startTime: startTime,
        endTime: endTime,
      };
    }

    // CONVERSÃO: API -> FullCalendar
    const convertApiDayToFullCalendar = (apiDay) => {
      const conversionMap = {
        0: 1, // Segunda-feira (API) -> Segunda-feira (FullCalendar)
        1: 2, // Terça-feira (API) -> Terça-feira (FullCalendar)
        2: 3, // Quarta-feira (API) -> Quarta-feira (FullCalendar)
        3: 4, // Quinta-feira (API) -> Quinta-feira (FullCalendar)
        4: 5, // Sexta-feira (API) -> Sexta-feira (FullCalendar)
        5: 6, // Sábado (API) -> Sábado (FullCalendar)
        6: 0, // Domingo (API) -> Domingo (FullCalendar)
      };
      return conversionMap[apiDay] !== undefined
        ? conversionMap[apiDay]
        : apiDay;
    };

    // Converter dias da API para formato FullCalendar
    const workingDays = apiWorkingDays.map(convertApiDayToFullCalendar);
    const allDays = [0, 1, 2, 3, 4, 5, 6];
    const hiddenDays = allDays.filter((day) => !workingDays.includes(day));

   
    return { hiddenDays, startTime, endTime };
  }, [
    user?.superuser_level,
    settings.current?.business_hours_settings?.working_days,
    settings.current?.business_hours_settings?.start_time,
    settings.current?.business_hours_settings?.end_time,
  ]);

  // Função para vibração tátil
  const triggerHapticFeedback = useCallback(
    (type = "light") => {
      if (!vibrationEnabled || !navigator.vibrate) return;

      switch (type) {
        case "light":
          navigator.vibrate(10);
          break;
        case "medium":
          navigator.vibrate(20);
          break;
        case "heavy":
          navigator.vibrate([10, 10, 10]);
          break;
        default:
          navigator.vibrate(10);
      }
    },
    [vibrationEnabled]
  );

  // Limpar estado do long press
  const clearLongPressState = useCallback(() => {
    if (longPressRef.current.timer) {
      clearTimeout(longPressRef.current.timer);
    }

    setLongPressState({
      isActive: false,
      timer: null,
      startTime: 0,
      startPosition: { x: 0, y: 0 },
      currentPosition: { x: 0, y: 0 },
      targetElement: null,
      dateInfo: null,
      hasMoved: false,
      scrollDetected: false,
    });

    longPressRef.current = {
      isActive: false,
      timer: null,
      startTime: 0,
      startPosition: { x: 0, y: 0 },
      currentPosition: { x: 0, y: 0 },
      targetElement: null,
      dateInfo: null,
      hasMoved: false,
      scrollDetected: false,
    };

    touchInterruptRef.current = false;
    isLongPressActiveRef.current = false; // *** NOVO: Limpar flag ***
  }, []);

  // Função para abrir modal de evento
  const openEventModal = useCallback(
    (dateStr, startTime = null, endTime = null) => {
      if (!canCreate) {
        Swal.fire("Sem permissão", "Você não tem permissão para criar agendamentos.", "warning");
        return;
      }

      // Usar horários específicos se fornecidos, senão usar padrões
      const defaultStart = startTime || "08:00";
      const defaultEnd = endTime || "09:00";

      reset({
        title: "",
        description: "",
        date: dateStr,
        start: defaultStart,
        end: defaultEnd,
        calendar: "",
        appointment_type: "",
        price: "",
        send_automations: false,
        disable_ai: true,
        client_name: "",
        client_phone: "",
        confirmation: "pending",
        status: "scheduled",
      });

      setSelectedInfo({
        selectedDate: dateStr,
      });

      setShowEventModal(true);
    },
    [reset]
  );

  // Função para verificar se o elemento é válido para long press
  const isValidLongPressTarget = useCallback((element) => {
    if (!element) return false;

    // Verificar se é um elemento de dia ou slot
    const validSelectors = [
      ".fc-daygrid-day",
      ".fc-timegrid-slot",
      ".fc-daygrid-day-number",
      ".fc-daygrid-day-top",
    ];

    return validSelectors.some(
      (selector) => element.matches(selector) || element.closest(selector)
    );
  }, []);

  // Obter informações de data do elemento
  const getDateInfoFromElement = useCallback(
    (element) => {
      // Tentar diferentes formas de obter a data
      let dateElement = element;

      // Se não for diretamente uma célula de dia, procurar o pai
      if (!dateElement.classList.contains("fc-daygrid-day")) {
        dateElement =
          element.closest(".fc-daygrid-day") ||
          element.closest(".fc-timegrid-slot");
      }

      if (!dateElement) return null;

      // Para células de dia
      if (dateElement.classList.contains("fc-daygrid-day")) {
        const dateStr = dateElement.getAttribute("data-date");
        return dateStr ? { dateStr } : null;
      }

      // Para slots de tempo
      if (dateElement.classList.contains("fc-timegrid-slot")) {
        const timeSlot = dateElement.getAttribute("data-time");
        const viewDate = fullCalendarRef?.view?.currentStart;

        if (timeSlot && viewDate) {
          const [hours, minutes] = timeSlot.split(":");
          const slotDate = new Date(viewDate);
          slotDate.setHours(parseInt(hours), parseInt(minutes), 0, 0);
          const dateStr = format(slotDate, "yyyy-MM-dd");
          return { dateStr, time: timeSlot };
        }
      }

      return null;
    },
    [fullCalendarRef]
  );

  const handleTouchStart = useCallback(
    (e) => {
      // Só processar se for mobile/tablet
      if (!isMobile && !isTablet) return;

      // Verificar se já há um long press ativo
      if (longPressRef.current.isActive || touchInterruptRef.current) {
        return;
      }

      const touch = e.touches[0];
      const target = e.target;

      // Verificar se é um target válido para long press
      if (!isValidLongPressTarget(target)) {
        return;
      }

      // Desabilitar long press no calendário mensal (usa DayEventsModal)
      const viewType = fullCalendarRef?.view?.type;
      if (viewType === "dayGridMonth") return;

      // Obter informações de data
      const dateInfo = getDateInfoFromElement(target);
      if (!dateInfo) {
        return;
      }

      const now = Date.now();
      const position = { x: touch.clientX, y: touch.clientY };

      // Configurar estado inicial
      const newState = {
        isActive: true,
        timer: null,
        startTime: now,
        startPosition: position,
        currentPosition: position,
        targetElement: target,
        dateInfo: dateInfo,
        hasMoved: false,
        scrollDetected: false,
      };

      // Configurar timer para long press
      const timer = setTimeout(() => {
        // Verificar se ainda é válido e não houve movimento significativo
        if (
          longPressRef.current.isActive &&
          !longPressRef.current.hasMoved &&
          !longPressRef.current.scrollDetected &&
          !touchInterruptRef.current
        ) {
          // *** CORREÇÃO: Definir flag ANTES de qualquer outra ação ***
          isLongPressActiveRef.current = true;

          // Trigger long press
          triggerHapticFeedback("medium");

          // Abrir modal de criação de evento
          const { dateStr, time } = longPressRef.current.dateInfo;
          const startTime = time ? time : "08:00";
          const endTimeDate = new Date();
          endTimeDate.setHours(
            parseInt(startTime.split(":")[0]),
            parseInt(startTime.split(":")[1]) + 20
          );
          const endTime = format(endTimeDate, "HH:mm");

          openEventModal(dateStr, startTime, endTime);

          // *** CORREÇÃO: Aumentar delay para garantir que modal abra ***
          setTimeout(() => {
            isLongPressActiveRef.current = false;
          }, 1000); // Aumentado para 1.5s para garantir

          // Limpar estado
          clearLongPressState();
        }
      }, 1000);

      newState.timer = timer;
      setLongPressState(newState);
      longPressRef.current = newState;
    },
    [
      isMobile,
      isTablet,
      isValidLongPressTarget,
      getDateInfoFromElement,
      triggerHapticFeedback,
      clearLongPressState,
      openEventModal,
    ]
  );

  //  handleTouchEnd com melhor prevenção de eventos
  const handleTouchEnd = useCallback(
    (e) => {
      if (!longPressRef.current.isActive) return;

      // Se o timer ainda está ativo, significa que foi um toque rápido
      if (longPressRef.current.timer) {
        clearLongPressState();
      }
    },
    [clearLongPressState]
  );

  // Touch Move - IMPLEMENTAÇÃO MELHORADA
  const handleTouchMove = useCallback(
    (e) => {
      if (!longPressRef.current.isActive) return;

      const touch = e.touches[0];
      const currentPosition = { x: touch.clientX, y: touch.clientY };

      // Calcular movimento
      const deltaX = Math.abs(
        currentPosition.x - longPressRef.current.startPosition.x
      );
      const deltaY = Math.abs(
        currentPosition.y - longPressRef.current.startPosition.y
      );
      const totalDistance = Math.sqrt(deltaX * deltaX + deltaY * deltaY);

      // Calcular velocidade (pixels por ms)
      const timeElapsed = Date.now() - longPressRef.current.startTime;
      const velocity = timeElapsed > 0 ? totalDistance / timeElapsed : 0;

      // Thresholds mais altos para permitir scroll
      const MOVEMENT_THRESHOLD = 30; // Aumentado de 20px para 30px
      const VERTICAL_SCROLL_THRESHOLD = 25; // Aumentado de 15px para 25px
      const VELOCITY_THRESHOLD = 0.5; // Aumentado de 0.3 para 0.5

      // Detectar movimento significativo ou scroll
      const hasMovedTooMuch = totalDistance > MOVEMENT_THRESHOLD;
      const isVerticalScroll =
        deltaY > VERTICAL_SCROLL_THRESHOLD && deltaY > deltaX;
      const isFastMovement = velocity > VELOCITY_THRESHOLD;

      // Se detectar scroll ou movimento significativo, cancelar long press
      if (hasMovedTooMuch || isVerticalScroll || isFastMovement) {
        longPressRef.current.hasMoved = true;
        longPressRef.current.scrollDetected = isVerticalScroll;
        clearLongPressState();
        return;
      }

      // Atualizar posição atual
      longPressRef.current.currentPosition = currentPosition;
      setLongPressState((prev) => ({
        ...prev,
        currentPosition: currentPosition,
      }));
    },
    [clearLongPressState]
  );

  // Scroll Handler - NOVO
  const handleScroll = useCallback(
    (e) => {
      if (longPressRef.current.isActive) {
        longPressRef.current.scrollDetected = true;
        clearLongPressState();
      }
    },
    [clearLongPressState]
  );

  // Context Menu Handler - NOVO (prevenir menu de contexto em long press)
  const handleContextMenu = useCallback(
    (e) => {
      if (isMobile || isTablet) {
        e.preventDefault();
        return false;
      }
    },
    [isMobile, isTablet]
  );


  // Configurar otimizações mobile
  useEffect(() => {
    if (!isMobile && !isTablet) return;

    const optimizePerformance = () => {
      // Detectar dispositivos com hardware limitado
      if (navigator.hardwareConcurrency < 4) {
        document.documentElement.style.setProperty(
          "--animation-duration",
          "0.1s"
        );
      }

      // Configurações de scroll otimizadas
      document.body.style.overscrollBehavior = "none";
      document.documentElement.style.overflowX = "hidden";
      document.body.style.overflowX = "hidden";
    };

    optimizePerformance();

    return () => {
      document.body.style.overscrollBehavior = "auto";
      document.documentElement.style.overflowX = "auto";
      document.body.style.overflowX = "auto";
    };
  }, [isMobile, isTablet]);

  // Configurar event listeners globais para touch
  useEffect(() => {
    if (!isMobile && !isTablet) return;

    // Listeners com passive apropriado
    document.addEventListener("touchstart", handleTouchStart, {
      passive: true,
    });
    document.addEventListener("touchmove", handleTouchMove, { passive: true });
    document.addEventListener("touchend", handleTouchEnd, { passive: true });
    document.addEventListener("scroll", handleScroll, { passive: true });
    document.addEventListener("contextmenu", handleContextMenu, {
      passive: false,
    });

    return () => {
      document.removeEventListener("touchstart", handleTouchStart);
      document.removeEventListener("touchmove", handleTouchMove);
      document.removeEventListener("touchend", handleTouchEnd);
      document.removeEventListener("scroll", handleScroll);
      document.removeEventListener("contextmenu", handleContextMenu);

      // Limpar qualquer timer ativo e flags
      clearLongPressState();
      isLongPressActiveRef.current = false;
    };
  }, [
    isMobile,
    isTablet,
    handleTouchStart,
    handleTouchMove,
    handleTouchEnd,
    handleScroll,
    handleContextMenu,
    clearLongPressState,
  ]);

  // Reset automations quando desmarcado
  useEffect(() => {
    if (!watchedSendAutomations) {
      setValue("client_name", "");
      setValue("client_phone", "");
    }
  }, [watchedSendAutomations, setValue]);

  // Função auxiliar para obter intervalo de datas válido
  const getValidDateRange = () => {
    if (currentDateRange.start && currentDateRange.end) {
      return {
        start: currentDateRange.start,
        end: currentDateRange.end,
      };
    } else {
      const now = new Date();
      const start = new Date(now.getFullYear(), now.getMonth(), 1);
      const end = new Date(now.getFullYear(), now.getMonth() + 1, 0);
      return {
        start: format(start, "yyyy-MM-dd'T'00:00:00"),
        end: format(end, "yyyy-MM-dd'T'00:00:00"),
      };
    }
  };

  // Função para remover timezone das strings de data
  const removeTimezone = (dateString) => {
    if (dateString && dateString.includes("+")) {
      return dateString.substring(0, dateString.indexOf("+"));
    } else if (
      dateString &&
      dateString.includes("-") &&
      dateString.lastIndexOf("-") > 10
    ) {
      return dateString.substring(0, dateString.lastIndexOf("-"));
    }
    return dateString;
  };

  // Buscar tipos de atendimento
  const fetchAppointmentTypes = async () => {
    try {
      const response = await api.get(APPOINTMENT_TYPES_URL + "active/");
      const types = response.data || [];
      setAppointmentTypes(types);
    } catch (error) {
      console.error("Erro ao carregar tipos de atendimento:", error);
      setAppointmentTypes([]);
    }
  };

  // Obter cor do evento
  const getEventColor = (event, calendarColor) => {
    if (event.appointment_type && event.appointment_type.color) {
      return event.appointment_type.color;
    }
    return calendarColor;
  };

  // Forçar recarregamento de eventos
  const forceRefreshEvents = async (startStr, endStr) => {
    setLoading(true);

    try {
      setEvents([]);

      const cleanStartStr = removeTimezone(startStr);
      const cleanEndStr = removeTimezone(endStr);

      const fetchPromises = selectedCalendars.map((calendarId) => {
        return api.get(CALENDAR_EVENTS_URL, {
          params: {
            start: cleanStartStr,
            end: cleanEndStr,
            calendar: calendarId,
          },
        });
      });

      const responses = await Promise.all(fetchPromises);
      let allEvents = [];

      responses.forEach((response, index) => {
        const calendarId = selectedCalendars[index];
        const calendarColor =
          calendarColors[calendarId] ||
          CALENDAR_COLORS[index % CALENDAR_COLORS.length];

        if (response.data && Array.isArray(response.data)) {
          const events = response.data.map((event) => {
            const eventColor = getEventColor(event, calendarColor);

            return {
              ...event,
              id: event.id,
              title: event.title,
              allDay: event.all_day,
              start: event.start,
              end: event.end,
              backgroundColor: eventColor,
              borderColor: eventColor,
              textColor: "#ffffff",
              classNames: "calendar-event",
              extendedProps: {
                calendarId: calendarId,
                calendarColor: calendarColor,
                description: event.description,
                calendar: event.calendar,
                appointment_type: event.appointment_type,
                confirmation: event.confirmation,
                status: event.status,
                price: event.price,
                // Nome real do cliente (vem de metadata.client_name no backend).
                // Usado no campo "Nome do Cliente" do modal de edição — sem
                // isso o frontend caía no fallback de mostrar o título inteiro.
                client_name: event.client_name,
                client_phone: event.client_phone,
                // Sinal de pagamento (null quando o agendamento não usa esse fluxo)
                payment_status: event.payment_status,
                payment_amount: event.payment_amount,
                has_payment_receipt: event.has_payment_receipt,
                id: event.id,
              },
            };
          });

          allEvents = [...allEvents, ...events];
        }
      });

      setEvents(allEvents);
    } catch (error) {
      console.error("Erro ao carregar eventos:", error);
      Swal.fire("Erro", "Falha ao carregar eventos.", "error");
    } finally {
      setLoading(false);
    }
  };

  // Inicialização
  useEffect(() => {
    fetchCalendars();
    fetchAppointmentTypes();
  }, []);

  // Carregar eventos quando calendários mudam
  useEffect(() => {
    if (selectedCalendars.length > 0) {
      const dateRange = getValidDateRange();
      forceRefreshEvents(dateRange.start, dateRange.end);
    } else {
      setEvents([]);
    }
  }, [selectedCalendars]);

  // Limpar tipo de atendimento ao trocar de agenda
  useEffect(() => {
    if (
      prevCalendarRef.current !== null &&
      prevCalendarRef.current !== selectedCalendar
    ) {
      const previousType = watch("appointment_type");
      setValue("appointment_type", "");
      if (previousType) {
        setValue("price", "");
      }
    }
    prevCalendarRef.current = selectedCalendar;
  }, [selectedCalendar, setValue, watch]);

  // Aplicar preço automaticamente
  useEffect(() => {
    if (selectedAppointmentType) {
      const appointmentType = appointmentTypes.find(
        (type) => type.id === parseInt(selectedAppointmentType)
      );
      if (appointmentType && appointmentType.price) {
        setValue("price", parseFloat(appointmentType.price));
      }
    }
  }, [selectedAppointmentType, appointmentTypes, setValue]);

  // Preencher formulário para edição
  useEffect(() => {
    if (selectedInfo?.eventId) {
      setValue("title", selectedInfo.title || "");
      setValue("description", selectedInfo.description || "");
      setValue("date", selectedInfo.startStr?.slice(0, 10) || "");
      setValue("start", selectedInfo.startStr?.slice(11, 16) || "");
      setValue("end", selectedInfo.endStr?.slice(11, 16) || "");
      // Sincroniza o ref antes do setValue para que o effect de reset não dispare
      prevCalendarRef.current = selectedInfo.calendar || "";
      setValue("calendar", selectedInfo.calendar || "");
      setValue("appointment_type", selectedInfo.appointment_type?.id || "");
      setValue("price", selectedInfo.price || "");
      setValue("confirmation", selectedInfo.confirmation || "pending");
      setValue("status", selectedInfo.status || "scheduled");

      setValue("send_automations", selectedInfo.send_automations || false);
      setValue("disable_ai", selectedInfo.disable_ai || false);
      // Preferir o client_name vindo do backend (metadata.client_name). Só cai
      // no title como fallback para eventos legados sem metadata.client_name.
      setValue("client_name", selectedInfo.client_name || selectedInfo.title || "");

      if (selectedInfo.client_phone) {
        let clientPhone = selectedInfo.client_phone;
        setValue("client_phone", clientPhone);
      }
    }
  }, [selectedInfo, setValue]);

  // Atribuir cores aos calendários
  const assignCalendarColors = (calendarList) => {
    const colors = {};
    calendarList.forEach((calendar, index) => {
      colors[calendar.id] = CALENDAR_COLORS[index % CALENDAR_COLORS.length];
    });
    setCalendarColors(colors);
  };

  // Handle date range change - CORRIGIDO COM STATUS INDICATORS
  const handleDateRangeChange = useCallback(
    (info) => {
      const cleanStartStr = removeTimezone(info.startStr);
      const cleanEndStr = removeTimezone(info.endStr);

      setCurrentDateRange({
        start: cleanStartStr,
        end: cleanEndStr,
      });

      const currentView = info.view;
      if (currentView) {
        setCurrentDate(new Date(currentView.currentStart));
        setMiniCalendarDate(new Date(currentView.currentStart));
      }

      forceRefreshEvents(cleanStartStr, cleanEndStr);

      // Legenda agora vive como componente React na sidebar (acima de
      // "Minhas agendas"), não mais injetada no toolbar.
    },
    [selectedCalendars, isMobile, isTablet]
  );

  // Editar evento do modal
  const handleEditFromModal = () => {
    if (!canUpdate) {
      Swal.fire("Sem permissão", "Você não tem permissão para editar agendamentos.", "warning");
      return;
    }

    const event = selectedEventForDetails;
    if (!event) return;

    setShowEventDetailsModal(false);

    const calendarId = event.extendedProps.calendar;
    const startDate = event.start;
    const endDate = event.end;

    const formatDateForForm = (date) => {
      return format(date, "yyyy-MM-dd'T'HH:mm");
    };
    setSelectedInfo({
      eventId: event.id,
      startStr: formatDateForForm(startDate),
      endStr: formatDateForForm(endDate),
      title: event.title,
      description: event.extendedProps.description || "",
      calendar: calendarId,
      appointment_type: event.extendedProps.appointment_type,
      price: event.extendedProps.price,
      confirmation: event.extendedProps.confirmation || "pending",
      client_name: event.extendedProps.client_name,
      client_phone: event.extendedProps.client_phone,
      send_automations: event.extendedProps.send_automations,
      disable_ai: event.extendedProps.disable_ai,
      status: event.extendedProps.status,
    });

    // Sincroniza o ref antes do setValue para que o effect de reset não dispare
    prevCalendarRef.current = calendarId;
    setValue("calendar", calendarId);
    setValue(
      "appointment_type",
      event.extendedProps.appointment_type?.id || ""
    );
    setValue("price", event.extendedProps.price || "");
    setValue("confirmation", event.extendedProps.confirmation || "pending");
    setShowEventModal(true);
  };

  // Deletar evento
  const handleDeleteFromModal = async () => {
    if (!canDelete) {
      Swal.fire("Sem permissão", "Você não tem permissão para cancelar agendamentos.", "warning");
      return;
    }

    const event = selectedEventForDetails;
    if (!event) return;

    setShowEventDetailsModal(false);

    const confirmDelete = await Swal.fire({
      title: "Tem certeza?",
      text: "Esta ação não poderá ser desfeita.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Sim, cancelar!",
      cancelButtonText: "Cancelar",
    });

    if (confirmDelete.isConfirmed) {
      try {
        const deleteUrl = `${CALENDAR_EVENTS_URL}${event.id}/`;
        await api.delete(deleteUrl);
        Swal.fire(
          "Cancelado!",
          "O agendamento foi cancelado com sucesso.",
          "success"
        );

        setTimeout(() => {
          const dateRange = getValidDateRange();
          forceRefreshEvents(dateRange.start, dateRange.end);
        }, 500);
      } catch (err) {
        console.error("Erro ao cancelar evento:", err);
        Swal.fire("Erro", "Não foi possível cancelar o evento.", "error");
      }
    }
  };

  // Buscar calendários
  const fetchCalendars = async () => {
    try {
      const response = await api.get(CALENDAR_URL);
      const list = response.data.results || response.data;
      setCalendars(list);
      assignCalendarColors(list);
      const allCalendarIds = list.map((c) => c.id);
      setSelectedCalendars(allCalendarIds);
    } catch (error) {
      console.error("Erro ao carregar agendas:", error);
    }
  };

  // Submit evento
  const onSubmitEvent = async (data) => {
    const isEditing = !!selectedInfo?.eventId;
    if (isEditing && !canUpdate) {
      Swal.fire("Sem permissão", "Você não tem permissão para editar agendamentos.", "warning");
      return;
    }
    if (!isEditing && !canCreate) {
      Swal.fire("Sem permissão", "Você não tem permissão para criar agendamentos.", "warning");
      return;
    }

    if (!selectedInfo?.selectedDate && !selectedInfo?.eventId) return;
    if (!selectedCalendars.length) return;

    const calendarId = data.calendar;
    if (!calendarId) {
      Swal.fire("Erro", "Por favor, selecione uma agenda", "error");
      return;
    }

    // Validação inteligente para automações e dados do cliente
    if (data.send_automations) {
      if (!data.client_name || !data.client_phone) {
        const result = await Swal.fire({
          title: "Dados do Cliente",
          text: "Para enviar automações automaticamente, é necessário informar o nome e telefone do cliente. Deseja desativar as automações e continuar?",
          icon: "info",
          showCancelButton: true,
          confirmButtonText: "Desativar automações",
          cancelButtonText: "Cancelar e preencher dados",
          reverseButtons: true
        });

        if (result.isConfirmed) {
          // Usuário escolheu desativar automações
          data.send_automations = false;
          setValue("send_automations", false);
        } else {
          // Usuário cancelou, manter modal aberto
          return;
        }
      }
    }

    setSubmitLoading(true);

    const start = `${data.date}T${data.start}`;
    const end = `${data.date}T${data.end}`;

    // console.log(data)
    // return

    const payload = {
      title: data.title,
      description: data.description,
      start: start,
      end: end,
      all_day: false,
      calendar: calendarId,
      is_recurring: data.is_recurring || false,
      recurrence_rule: data.is_recurring ? "WEEKLY" : null,
      manual_schedule: 1,
      uid: bottyData?.uid || null,
      price: data.price || null,
      confirmation: data.confirmation || "pending",
      status: data.status || "scheduled",
      tags: data.tags || "",
      send_automations: Boolean(data.send_automations) ? 1 : 0,
      disable_ai: Boolean(data.disable_ai) ? 1 : 0,
      ...(data.send_automations && {
        client_name: data.client_name,
        client_phone: data.client_phone,
      }),
      ...(data.appointment_type && {
        appointment_type: data.appointment_type,
      }),
    };

    // console.log(payload);
    // return;

    try {
      if (selectedInfo?.eventId) {
        await api.put(
          CALENDAR_EVENTS_URL + `${selectedInfo.eventId}/`,
          payload
        );
        Swal.fire("Atualizado", "Evento atualizado com sucesso", "success");
      } else {
        await api.post(
          `${CALENDAR_EVENTS_URL}create_manual_appointment/`,
          payload
        );
        Swal.fire("Criado", "Evento criado com sucesso", "success");
      }

      setShowEventModal(false);
      reset();
      setSelectedInfo(null);
      // setSendAutomations(false);
      // *** NOVO: Limpar flag ***
      isLongPressActiveRef.current = false;

      setTimeout(() => {
        const dateRange = getValidDateRange();
        forceRefreshEvents(dateRange.start, dateRange.end);
      }, 500);
    } catch (error) {
      console.error("Erro ao salvar evento:", error);
      if (error.response.data?.non_field_errors) {
        Swal.fire("Oops!", error.response.data?.non_field_errors[0], "warning");
      } else if (error.response?.data) {
        Swal.fire("Erro", error.response.data, "error");
      } else {
        Swal.fire("Erro", "Não foi possível salvar o evento", "error");
      }
    } finally {
      setSubmitLoading(false);
    }
  };

  // Submit calendário
  const onSubmitCalendar = async (data) => {
    if (!canCreate) {
      Swal.fire("Sem permissão", "Você não tem permissão para criar agendas.", "warning");
      return;
    }

    data.user = user.user_id;
    try {
      await api.post(CALENDAR_URL, data);
      await fetchCalendars();
      setShowCalendarModal(false);
      resetCalendar();
      setShowBottomSheet(false);
      Swal.fire("Criado", "Calendário criado com sucesso", "success");
    } catch (error) {
      console.error("Erro ao criar calendário:", error);
      
      // Verifica se a resposta da API tem o formato esperado
      const errorData = error.response?.data;
      let title = "Erro";
      let message = "Não foi possível criar o calendário";
      let icon = "error";
      
      if (errorData) {
        // Se a API retornou dados estruturados
        if (errorData.message) {
          message = errorData.message;
        } else if (errorData.detail) {
          message = errorData.detail;
        } else if (typeof errorData === 'string') {
          message = errorData;
        }
        
        // Tratamento específico para diferentes tipos de erro
        if (errorData.error) {
          switch (errorData.error) {
            case "CALENDAR_LIMIT_EXCEEDED":
              title = "Limite de Agendas Atingido";
              icon = "warning";
              break;
            case "INVALID_DATA":
              title = "Dados Inválidos";
              break;
            case "PERMISSION_DENIED":
              title = "Permissão Negada";
              break;
            default:
              title = "Erro";
          }
        }
      } else if (error.message) {
        // Fallback para erros de rede ou outros
        message = error.message;
      }
      
      Swal.fire({
        title: title,
        text: message,
        icon: icon,
        confirmButtonText: "OK"
      });
    }
  };

  // Função para abrir modal de edição
  const handleEditCalendar = (calendar) => {
    setEditingCalendar(calendar);
    setEditCalendarValue("name", calendar.name);
    setEditCalendarValue("is_shared", calendar.is_shared);
    setShowEditCalendarModal(true);
    fetchManualReminderOptions(calendar.id);
  };

  // Carrega as automações dos nós de scheduling 2.0 que apontam para esta
  // agenda + a seleção já salva. Agendamento manual nunca passa pela tool,
  // então o dono da agenda escolhe aqui o que disparar.
  const fetchManualReminderOptions = async (calendarId) => {
    setManualReminderOptions([]);
    setSelectedReminderKeys([]);
    setLoadingManualReminders(true);
    try {
      const response = await api.get(
        `${CALENDAR_URL}${calendarId}/manual-reminders/`
      );
      setManualReminderOptions(response.data?.options || []);
      setSelectedReminderKeys(response.data?.selected_keys || []);
    } catch (error) {
      console.error("Erro ao carregar automações da agenda:", error);
      setManualReminderOptions([]);
      setSelectedReminderKeys([]);
    } finally {
      setLoadingManualReminders(false);
    }
  };

  const toggleReminderKey = (key) => {
    setSelectedReminderKeys((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    );
  };

  // Rótulo legível do gatilho/offset de um lembrete.
  const formatReminderTrigger = (option) => {
    const minutes = option.offset_minutes || 0;
    switch (option.trigger) {
      case "after_creation":
        return `${minutes} min após criar o agendamento`;
      case "after_event":
        return `${minutes} min após o evento`;
      case "before_event":
      default:
        return `${minutes} min antes do evento`;
    }
  };

  // Submit edição de calendário
  const onSubmitEditCalendar = async (data) => {
    if (!canUpdate) {
      Swal.fire("Sem permissão", "Você não tem permissão para editar agendas.", "warning");
      return;
    }

    if (!editingCalendar) return;

    try {
      await api.put(`${CALENDAR_URL}${editingCalendar.id}/`, {
        ...data,
        user: user.user_id,
      });
      // Salva a seleção de automações do agendamento manual (só as keys —
      // o backend reconstrói o snapshot a partir do grafo).
      await api.post(`${CALENDAR_URL}${editingCalendar.id}/manual-reminders/`, {
        selected_keys: selectedReminderKeys,
      });
      await fetchCalendars();
      setShowEditCalendarModal(false);
      setEditingCalendar(null);
      resetEditCalendar();
      setShowBottomSheet(false);
      Swal.fire("Atualizado", "Calendário atualizado com sucesso", "success");
    } catch (error) {
      console.error("Erro ao atualizar calendário:", error);
      
      // Verifica se a resposta da API tem o formato esperado
      const errorData = error.response?.data;
      let title = "Erro";
      let message = "Não foi possível atualizar o calendário";
      let icon = "error";
      
      if (errorData) {
        // Se a API retornou dados estruturados
        if (errorData.message) {
          message = errorData.message;
        } else if (errorData.detail) {
          message = errorData.detail;
        } else if (typeof errorData === 'string') {
          message = errorData;
        }
        
        // Tratamento específico para diferentes tipos de erro
        if (errorData.error) {
          switch (errorData.error) {
            case "CALENDAR_NOT_FOUND":
              title = "Agenda Não Encontrada";
              break;
            case "INVALID_DATA":
              title = "Dados Inválidos";
              break;
            case "PERMISSION_DENIED":
              title = "Permissão Negada";
              break;
            default:
              title = "Erro";
          }
        }
      } else if (error.message) {
        // Fallback para erros de rede ou outros
        message = error.message;
      }
      
      Swal.fire({
        title: title,
        text: message,
        icon: icon,
        confirmButtonText: "OK"
      });
    }
  };

  // Função para deletar calendário
  const handleDeleteCalendar = async () => {
    if (!canDelete) {
      Swal.fire("Sem permissão", "Você não tem permissão para excluir agendas.", "warning");
      return;
    }

    if (!editingCalendar) return;

    const calName = editingCalendar.name || "esta agenda";
    const result = await Swal.fire({
      title: "⚠️ Excluir agenda DEFINITIVAMENTE?",
      html: (
        `Você está prestes a apagar a agenda <strong>"${calName}"</strong> ` +
        "junto com <strong>TODO o histórico de agendamentos</strong> vinculados.<br><br>" +
        "<strong style='color:#dc2626'>Esta ação é IRREVERSÍVEL.</strong> " +
        "Dados financeiros (pagamentos, comprovantes), histórico de atendimentos e " +
        "registros de clientes desta agenda serão perdidos permanentemente.<br><br>" +
        "Se você só quer ocultá-la, use <em>Arquivar</em>.<br><br>" +
        `Para confirmar, digite o nome da agenda: <code>${calName}</code>`
      ),
      icon: "warning",
      input: "text",
      inputPlaceholder: "Digite o nome exato",
      showCancelButton: true,
      confirmButtonColor: "#dc2626",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Excluir definitivamente",
      cancelButtonText: "Cancelar",
      preConfirm: (value) => {
        if (value !== calName) {
          Swal.showValidationMessage("Nome digitado não confere com o nome da agenda.");
          return false;
        }
        return true;
      },
    });

    if (result.isConfirmed) {
      try {
        await api.delete(`${CALENDAR_URL}${editingCalendar.id}/`);
        await fetchCalendars();
        setShowEditCalendarModal(false);
        setEditingCalendar(null);
        resetEditCalendar();
        setShowBottomSheet(false);
        Swal.fire("Excluída!", "A agenda foi excluída com sucesso.", "success");
      } catch (error) {
        console.error("Erro ao deletar calendário:", error);
        
        // Verifica se a resposta da API tem o formato esperado
        const errorData = error.response?.data;
        let title = "Erro";
        let message = "Não foi possível excluir a agenda";
        let icon = "error";
        
        if (errorData) {
          // Se a API retornou dados estruturados
          if (errorData.message) {
            message = errorData.message;
          } else if (errorData.detail) {
            message = errorData.detail;
          } else if (typeof errorData === 'string') {
            message = errorData;
          }
          
          // Tratamento específico para diferentes tipos de erro
          if (errorData.error) {
            switch (errorData.error) {
              case "CALENDAR_NOT_FOUND":
                title = "Agenda Não Encontrada";
                break;
              case "CALENDAR_HAS_EVENTS":
                title = "Agenda Possui Agendamentos";
                icon = "warning";
                break;
              case "PERMISSION_DENIED":
                title = "Permissão Negada";
                break;
              default:
                title = "Erro";
            }
          }
        } else if (error.message) {
          // Fallback para erros de rede ou outros
          message = error.message;
        }
        
        Swal.fire({
          title: title,
          text: message,
          icon: icon,
          confirmButtonText: "OK"
        });
      }
    }
  };

  // Event drop
  const handleEventDrop = async (info) => {
    if (!canUpdate) {
      Swal.fire("Sem permissão", "Você não tem permissão para mover agendamentos.", "warning");
      info.revert();
      return;
    }

    const { id, title, extendedProps, start, end } = info.event;
    const calendarId = extendedProps.calendar;

    const formatDateWithoutTimezone = (date) => {
      return format(date, "yyyy-MM-dd'T'HH:mm:ss");
    };

    const payload = {
      title: title,
      description: extendedProps.description || "",
      start: formatDateWithoutTimezone(start),
      end: end ? formatDateWithoutTimezone(end) : null,
      all_day: info.event.allDay,
      calendar: calendarId,
      manual_schedule: 1,
      appointment_type: extendedProps.appointment_type?.id || null,
      price: extendedProps.price || null,
      confirmation: extendedProps.confirmation || "pending",
      status: extendedProps.status || "scheduled",
      client_phone:
        extendedProps?.client_phone || extendedProps.metadata?.phone || "",
    };
    try {
      await api.put(CALENDAR_EVENTS_URL + `${id}/`, payload);

      setTimeout(() => {
        const dateRange = getValidDateRange();
        forceRefreshEvents(dateRange.start, dateRange.end);
        Swal.fire("Sucesso", "Evento atualizado!", "success");
      }, 500);
    } catch (error) {
      console.error("Erro ao mover evento:", error);
      Swal.fire("Erro", "Não foi possível mover o evento", "error");
      info.revert();
    }
  };

  // Toggle calendário
  const handleCheckboxToggle = (calendarId) => {
    setSelectedCalendars((prev) =>
      prev.includes(calendarId)
        ? prev.filter((id) => id !== calendarId)
        : [...prev, calendarId]
    );
  };

  const handleDateSelect = (info) => {
    const formattedDate = info.startStr.slice(0, 10);

    if (isLongPressActiveRef.current) {
      info.jsEvent?.preventDefault();
      info.jsEvent?.stopPropagation();
      return;
    }

    if (isMobile || isTablet) {
      // *** IMPORTANTE: Prevenir qualquer mudança de view não intencional ***
      if (info.jsEvent) {
        info.jsEvent.preventDefault();
        info.jsEvent.stopPropagation();
      }
      return;
    } else {
      // Desktop: sempre abrir modal
      openEventModal(formattedDate);
    }
  };

  // Handle day click in month view - show day events modal
  const handleDateClick = (info) => {
    if (!isMobile && !isTablet) return;

    const viewType = fullCalendarRef?.view?.type;
    if (viewType !== "dayGridMonth") return;

    if (info.jsEvent) {
      info.jsEvent.preventDefault();
      info.jsEvent.stopPropagation();
    }

    triggerHapticFeedback("light");
    setSelectedDayForEvents(info.dateStr);
    setShowDayEventsModal(true);
  };

  // Handle clicking an event from the day events modal
  const handleDayEventClick = (event) => {
    setShowDayEventsModal(false);
    // Ensure start/end are Date objects for compatibility with EventDetailsModal
    setSelectedEventForDetails({
      ...event,
      start: new Date(event.start),
      end: new Date(event.end),
    });
    setShowEventDetailsModal(true);
  };

  // Handle event click - CORRIGIDO PARA CAPTURAR HORÁRIO DOS SLOTS
  const handleEventClick = (clickInfo) => {
    // Prevenir propagação
    if (clickInfo.jsEvent) {
      clickInfo.jsEvent.preventDefault();
      clickInfo.jsEvent.stopPropagation();
    }

    // Se for slot livre, abrir modal de criação COM O HORÁRIO CORRETO
    if (clickInfo.event.extendedProps?.isFreeSlot) {
      // Capturar o horário do slot
      const slotStart = new Date(clickInfo.event.start);
      const slotEnd = new Date(clickInfo.event.end);

      // Formatar para HH:MM
      const startTime = format(slotStart, "HH:mm");
      const endTime = format(slotEnd, "HH:mm");
      const slotDate = format(slotStart, "yyyy-MM-dd");
      // Abrir modal com o horário específico do slot
      openEventModal(slotDate, startTime, endTime);
      return;
    }

    // Verificar ID do evento
    const eventId = clickInfo.event.id;
    if (!eventId || eventId.toString().startsWith("free-slot-")) {
      console.error("Invalid event ID:", eventId);
      Swal.fire("Erro", "Este evento não pode ser editado", "error");
      return;
    }

    // Trigger haptic feedback se mobile
    if (isMobile || isTablet) {
      triggerHapticFeedback("light");
    }

    // Abrir modal de detalhes
    try {
      setSelectedEventForDetails(clickInfo.event);
      setShowEventDetailsModal(true);
    } catch (error) {
      console.error("Error opening modal:", error);
      Swal.fire("Erro", "Erro ao abrir detalhes do evento", "error");
    }
  };

  const createFreeSlots = useCallback(() => {
    // Slots livres para views de dia e semana (incluindo mobile)
    const businessHoursSettings = settings.current?.business_hours_settings;
    const appointmentDuration =
      businessHoursSettings?.appointment_duration || 30;
    const appointmentInterval =
      businessHoursSettings?.appointment_interval || 0;
    const slotDurationMinutes = appointmentDuration + appointmentInterval;

    const viewType = fullCalendarRef?.view?.type;
    if (!viewType || viewType === "dayGridMonth") return [];

    const currentView = fullCalendarRef.view;
    const slots = [];

    // Determinar quais dias processar baseado na view
    let daysToProcess = [];

    if (viewType === "timeGridDay") {
      // Day view: apenas o dia atual
      daysToProcess = [new Date(currentView.currentStart)];
    } else if (viewType === "timeGridWeek") {
      // Week view: todos os dias da semana
      const startOfWeek = new Date(currentView.currentStart);
      const endOfWeek = new Date(currentView.currentEnd);

      // Gerar array com todos os dias da semana
      for (
        let d = new Date(startOfWeek);
        d < endOfWeek;
        d.setDate(d.getDate() + 1)
      ) {
        daysToProcess.push(new Date(d));
      }
    }

    console.log(`Processing ${daysToProcess.length} days for free slots`);

    // Processar cada dia
    daysToProcess.forEach((dayDate) => {
      // Configurar horários para este dia específico
      const startTime = new Date(dayDate);
      startTime.setHours(6, 0, 0, 0); // 06:00

      const endTime = new Date(dayDate);
      endTime.setHours(23, 0, 0, 0); // 22:00

      // Criar slots para este dia
      for (
        let time = new Date(startTime);
        time < endTime;
        time.setMinutes(time.getMinutes() + slotDurationMinutes)
      ) {
        const slotStart = new Date(time);
        const slotEnd = new Date(time.getTime() + slotDurationMinutes * 60000);

        // Verificar se há conflito com eventos existentes NESTE DIA ESPECÍFICO
        const hasConflict = events.some((event) => {
          const eventStart = new Date(event.start);
          const eventEnd = new Date(event.end);

          // Verificar se o evento é do mesmo dia
          const isSameDay =
            eventStart.toDateString() === slotStart.toDateString();

          if (!isSameDay) return false;

          // Verificar conflito de horário
          return (
            (slotStart >= eventStart && slotStart < eventEnd) ||
            (slotEnd > eventStart && slotEnd <= eventEnd) ||
            (slotStart <= eventStart && slotEnd >= eventEnd)
          );
        });

        if (!hasConflict) {
          slots.push({
            id: `free-slot-${slotStart.getTime()}`,
            title: "LIVRE",
            start: slotStart.toISOString(),
            end: slotEnd.toISOString(),
            display: "background",
            backgroundColor: "transparent",
            borderColor: "#d1d5db",
            textColor: "#9ca3af",
            classNames: ["free-slot"],
            extendedProps: {
              isFreeSlot: true,
              slotStart: slotStart.toISOString(),
              slotEnd: slotEnd.toISOString(),
            },
          });
        }
      }
    });

    return slots;
  }, [events, fullCalendarRef, settings]);

  // Combinar eventos com slots livres
  const allEventsWithSlots = useMemo(() => {
    const freeSlots = createFreeSlots();
    const combined = [...events, ...freeSlots];

    return combined;
  }, [events, createFreeSlots]);

  // Componentes auxiliares
  const CalendarTypesAccordion = ({ calId }) => {
    const types = typesByCalendar[calId] || [];
    if (types.length === 0) return null;
    const isOpen = expandedCalendars.has(calId);
    return (
      <div className={`cal-types-panel${isOpen ? ' cal-types-panel--open' : ''}`}>
        {types.map((type) => (
          <div key={type.id} className="cal-type-row">
            <div className="cal-type-dot" style={{ backgroundColor: type.color || '#ccc' }} />
            <div className="cal-type-content">
              <span className="cal-type-name">{type.name}</span>
              <span className="cal-type-meta">
                {type.duration}min{type.price ? ` · ${formatCurrency(type.price)}` : ''}
              </span>
            </div>
          </div>
        ))}
      </div>
    );
  };

  const handleCheckboxToggleWithFeedback = (calendarId) => {
    triggerHapticFeedback("light");
    handleCheckboxToggle(calendarId);
  };

  const handleOpenBottomSheet = () => {
    triggerHapticFeedback("light");
    setShowBottomSheet(true);
    document.body.style.overflow = "hidden";
    document.body.style.height = "100vh";
  };

  const handleCloseBottomSheet = () => {
    triggerHapticFeedback("light");
    setShowBottomSheet(false);
    document.body.style.overflow = "auto";
    document.body.style.height = "auto";
  };

  const handleNewCalendar = () => {
    triggerHapticFeedback("medium");
    setShowCalendarModal(true);
    if (isMobile || isTablet) {
      setShowBottomSheet(false);
    }
  };

  // Mini Calendar functions
  const navigateMiniCalendar = (direction) => {
    const newDate = new Date(miniCalendarDate);
    if (direction === "prev") {
      newDate.setMonth(newDate.getMonth() - 1);
    } else {
      newDate.setMonth(newDate.getMonth() + 1);
    }
    setMiniCalendarDate(newDate);
  };

  const handleMiniCalendarDayClick = (date) => {
    if (fullCalendarRef) {
      fullCalendarRef.gotoDate(date);
      setCurrentDate(date);
    }
  };

  const generateMiniCalendarDays = () => {
    const start = startOfMonth(miniCalendarDate);
    const end = endOfMonth(miniCalendarDate);
    const startDay = start.getDay();
    const adjustedStart = new Date(start);
    adjustedStart.setDate(adjustedStart.getDate() - startDay);
    const endDay = end.getDay();
    const adjustedEnd = new Date(end);
    adjustedEnd.setDate(adjustedEnd.getDate() + (6 - endDay));
    return eachDayOfInterval({ start: adjustedStart, end: adjustedEnd });
  };

  const MiniCalendar = () => {
    const days = generateMiniCalendarDays();
    const monthYear = format(miniCalendarDate, "MMMM yyyy", { locale: ptBR });

    return (
      <div className="mini-calendar">
        <div className="mini-calendar-header">
          <button
            onClick={() => navigateMiniCalendar("prev")}
            className="mini-calendar-nav"
          >
            ‹
          </button>
          <span className="mini-calendar-title">{monthYear}</span>
          <button
            onClick={() => navigateMiniCalendar("next")}
            className="mini-calendar-nav"
          >
            ›
          </button>
        </div>

        <div className="mini-calendar-weekdays">
          {["D", "S", "T", "Q", "Q", "S", "S"].map((day, index) => (
            <div key={index} className="mini-calendar-weekday">
              {day}
            </div>
          ))}
        </div>

        <div className="mini-calendar-days">
          {days.map((day, index) => {
            const isCurrentMonth =
              day.getMonth() === miniCalendarDate.getMonth();
            const isTodayDate = isToday(day);
            const isSelected = isSameDay(day, currentDate);

            return (
              <button
                key={index}
                className={`mini-calendar-day ${
                  !isCurrentMonth ? "other-month" : ""
                } ${isTodayDate ? "today" : ""} ${
                  isSelected ? "selected" : ""
                }`}
                onClick={() => handleMiniCalendarDayClick(day)}
              >
                {format(day, "d")}
              </button>
            );
          })}
        </div>
      </div>
    );
  };

  const MobileFloatingButton = () => (
    <button
      className={`mobile-calendar-toggle ${showBottomSheet ? "active" : ""}`}
      onClick={handleOpenBottomSheet}
      type="button"
      aria-label="Abrir menu de calendários"
    >
      <span className="icon">{showBottomSheet ? "✕" : "📅"}</span>
    </button>
  );

  const MobileBottomSheet = () => (
    <>
      <div
        className={`calendar-bottom-sheet-overlay ${
          showBottomSheet ? "show" : ""
        }`}
        onClick={handleCloseBottomSheet}
      />
      <div className={`calendar-bottom-sheet ${showBottomSheet ? "show" : ""}`}>
        <div className="calendar-bottom-sheet-header">
          <div className="calendar-bottom-sheet-handle" />
          <h3 className="calendar-bottom-sheet-title">
            Minhas Agendas
            <span style={{ display: 'inline-flex', gap: 4, marginLeft: 'auto' }}>
              <button
                type="button"
                onClick={() => setShowArchivedListModal(true)}
                title="Ver agendas arquivadas"
                aria-label="Ver agendas arquivadas"
                style={{
                  background: 'transparent', border: 'none', padding: 4,
                  color: '#9ca3af', cursor: 'pointer', display: 'inline-flex',
                  alignItems: 'center',
                }}
              >
                <IconArchive size={18} />
              </button>
              <button
                className="calendar-bottom-sheet-close"
                onClick={handleCloseBottomSheet}
                type="button"
                aria-label="Fechar menu"
              >
                ✕
              </button>
            </span>
          </h3>
        </div>

        <div className="calendar-bottom-sheet-content">
          {calendars.map((cal) => {
            const hasTypes = (typesByCalendar[cal.id] || []).length > 0;
            const isExpanded = expandedCalendars.has(cal.id);
            return (
              <div key={cal.id}>
                <div className="calendar-bottom-sheet-item">
                  <div
                    className="calendar-bottom-sheet-item-left"
                    onClick={() => handleCheckboxToggleWithFeedback(cal.id)}
                  >
                    <Form.Check
                      type="checkbox"
                      className="calendar-bottom-sheet-checkbox"
                      checked={selectedCalendars.includes(cal.id)}
                      onChange={() => {}}
                      style={{ pointerEvents: "none" }}
                    />
                    <div
                      className="calendar-bottom-sheet-color"
                      style={{ backgroundColor: calendarColors[cal.id] || "#ccc" }}
                    />
                    <span className="calendar-bottom-sheet-text">
                      {cal.name}
                      {cal.is_shared && (
                        <span className="calendar-bottom-sheet-shared">compartilhado</span>
                      )}
                    </span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                    {hasTypes && (
                      <button
                        type="button"
                        className="cal-expand-btn"
                        onClick={() => toggleCalendarTypes(cal.id)}
                        title={isExpanded ? 'Ocultar tipos' : 'Ver tipos de atendimento'}
                      >
                        <span className="cal-type-badge">{typesByCalendar[cal.id].length}</span>
                        <IconChevronDown
                          size={12}
                          className={`cal-chevron${isExpanded ? ' cal-chevron--open' : ''}`}
                        />
                      </button>
                    )}
                    {canUpdate && (
                      <button
                        type="button"
                        className="calendar-bottom-sheet-edit-btn"
                        onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleEditCalendar(cal); }}
                        title="Editar calendário"
                      >
                        <IconEdit size={16} />
                      </button>
                    )}
                  </div>
                </div>
                <CalendarTypesAccordion calId={cal.id} />
              </div>
            );
          })}
        </div>

        {canCreate && (
        <div className="calendar-bottom-sheet-actions">
          <button
            className="calendar-bottom-sheet-new-btn"
            onClick={handleNewCalendar}
            type="button"
          >
            + Nova Agenda
          </button>
        </div>
        )}
      </div>
    </>
  );

  const createStatusIcon = (status) => {
    const statusConfig = {
      pending: {
        icon: `<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="#ff9900"><path stroke="none" d="M0 0h24v24H0z" fill="none"/><path d="M12 2c5.523 0 10 4.477 10 10a10 10 0 0 1 -19.995 .324l-.005 -.324l.004 -.28c.148 -5.393 4.566 -9.72 9.996 -9.72zm0 9h-1l-.117 .007a1 1 0 0 0 0 1.986l.117 .007v3l.007 .117a1 1 0 0 0 .876 .876l.117 .007h1l.117 -.007a1 1 0 0 0 .876 -.876l.007 -.117l-.007 -.117a1 1 0 0 0 -.764 -.857l-.112 -.02l-.117 -.006v-3l-.007 -.117a1 1 0 0 0 -.876 -.876l-.117 -.007zm.01 -3l-.127 .007a1 1 0 0 0 0 1.986l.117 .007l.127 -.007a1 1 0 0 0 0 -1.986l-.117 -.007z" /></svg>`,
        color: "#ff9900",
      },
      confirmed: {
        icon: `<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="#28a745"><path stroke="none" d="M0 0h24v24H0z" fill="none"/><path d="M17 3.34a10 10 0 1 1 -14.995 8.984l-.005 -.324l.005 -.324a10 10 0 0 1 14.995 -8.336zm-1.293 5.953a1 1 0 0 0 -1.32 -.083l-.094 .083l-3.293 3.292l-1.293 -1.292l-.094 -.083a1 1 0 0 0 -1.403 1.403l.083 .094l2 2l.094 .083a1 1 0 0 0 1.226 0l.094 -.083l4 -4l.083 -.094a1 1 0 0 0 -.083 -1.32z" /></svg>`,
        color: "#28a745",
      },
      canceled: {
        icon: `<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="#dc3545" class="icon icon-tabler icons-tabler-filled icon-tabler-circle-x"><path stroke="none" d="M0 0h24v24H0z" fill="none"/><path d="M17 3.34a10 10 0 1 1 -14.995 8.984l-.005 -.324l.005 -.324a10 10 0 0 1 14.995 -8.336zm-6.489 5.8a1 1 0 0 0 -1.218 1.567l1.292 1.293l-1.292 1.293l-.083 .094a1 1 0 0 0 1.497 1.32l1.293 -1.292l1.293 1.292l.094 .083a1 1 0 0 0 1.32 -1.497l-1.292 -1.293l1.292 -1.293l.083 -.094a1 1 0 0 0 -1.497 -1.32l-1.293 1.292l-1.293 -1.292l-.094 -.083z" /></svg>`,
        color: "#dc3545",
      },
      not_confirmed: {
        icon: `<svg  xmlns="http://www.w3.org/2000/svg"  width="20"  height="20"  viewBox="0 0 24 24"  fill="#6c757d"  class="icon icon-tabler icons-tabler-filled icon-tabler-alert-triangle"><path stroke="none" d="M0 0h24v24H0z" fill="none"/><path d="M12 1.67c.955 0 1.845 .467 2.39 1.247l.105 .16l8.114 13.548a2.914 2.914 0 0 1 -2.307 4.363l-.195 .008h-16.225a2.914 2.914 0 0 1 -2.582 -4.2l.099 -.185l8.11 -13.538a2.914 2.914 0 0 1 2.491 -1.403zm.01 13.33l-.127 .007a1 1 0 0 0 0 1.986l.117 .007l.127 -.007a1 1 0 0 0 0 -1.986l-.117 -.007zm-.01 -7a1 1 0 0 0 -.993 .883l-.007 .117v4l.007 .117a1 1 0 0 0 1.986 0l.007 -.117v-4l-.007 -.117a1 1 0 0 0 -.993 -.883z" /></svg>`,
        color: "#6c757d",
      },
      missing: {
        icon: `<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#6c757d" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path stroke="none" d="M0 0h24v24H0z" fill="none"/><path d="M19 19m-2 0a2 2 0 1 0 4 0a2 2 0 1 0 -4 0" /><path d="M21 21l-2 -2" /><path d="M6 21v-2a4 4 0 0 1 4 -4h2.5" /><path d="M16 7a4 4 0 1 1 -8 0a4 4 0 0 1 8 0" /></svg>`,
        color: "#6c757d",
      },
    };

    const config = statusConfig[status];
    if (!config) return "";

    return `
      <div style="
        position: absolute;
        top: -2px;
        right: -2px;
        width: 22.5px;
        height: 22.5px;
        background: white;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        box-shadow: rgba(14, 30, 37, 0.12) 0px 2px 4px 0px, rgba(14, 30, 37, 0.32) 0px 2px 16px 0px;
        z-index: 10;
        pointer-events: none;
      ">
        ${config.icon}
      </div>
    `;
  };

  // Render custom day headers for mobile (replaces DOM manipulation)
  const renderDayHeader = useCallback((arg) => {
    if (!isMobile && !isTablet) return undefined;

    const { date, isToday: todayFlag, view } = arg;
    const viewType = view.type;

    if (viewType === "timeGridWeek") {
      const dayLetter = format(date, "EEEEE", { locale: ptBR }).toUpperCase();
      const dayNum = format(date, "d");
      return (
        <div className="m-week-header">
          <span className="m-week-letter">{dayLetter}</span>
          <span className={`m-week-num${todayFlag ? " m-today" : ""}`}>
            {dayNum}
          </span>
        </div>
      );
    }

    if (viewType === "timeGridDay") {
      const dayNum = format(date, "d");
      const dayName = format(date, "EEEE", { locale: ptBR });
      return (
        <div className="m-day-header">
          <span className={`m-day-num${todayFlag ? " m-today" : ""}`}>
            {dayNum}
          </span>
          <span className="m-day-name">{dayName}</span>
        </div>
      );
    }

    if (viewType === "dayGridMonth") {
      const dayLetter = format(date, "EEEEE", { locale: ptBR }).toUpperCase();
      return <span className="m-month-header">{dayLetter}</span>;
    }

    return undefined;
  }, [isMobile, isTablet]);

  return (
    <div className="calendar-wrapper">
      <Row>
        {/* Sidebar Desktop */}
        {!isMobile && !isTablet && (
          <Col md={3} className="sidebar-calendar">
            {canCreate ? (
              <Button
                variant="primary"
                className="w-100 mb-4 create-button"
                onClick={() => setShowCalendarModal(true)}
              >
                <span className="create-icon">+</span>
                Nova agenda
              </Button>
            ) : (
              <h6 className="px-2">Navegue pelo calendário</h6>
            )}

            <MiniCalendar />

            {/* Legenda dos status dos agendamentos. Explica os 4 ícones
                que aparecem em cada evento do calendário. */}
            <div className="event-status-legend">
              <h6 className="event-status-legend__title">Status dos agendamentos</h6>
              <div className="event-status-legend__items">
                {Object.entries(EVENT_LEGEND_STATUS).map(([key, status]) => {
                  const IconComp = status.Icon;
                  return (
                    <div
                      key={key}
                      className="event-status-legend__item"
                      title={status.label}
                    >
                      <IconComp size={16} color={status.color} />
                      <span className="event-status-legend__label">
                        {status.label}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="calendars-section">
              <h6 className="calendars-title" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <span>Minhas agendas</span>
                <button
                  type="button"
                  onClick={() => setShowArchivedListModal(true)}
                  title="Ver agendas arquivadas"
                  aria-label="Ver agendas arquivadas"
                  style={{
                    background: 'transparent', border: 'none', padding: 4,
                    color: '#9ca3af', cursor: 'pointer', display: 'inline-flex',
                    alignItems: 'center',
                  }}
                >
                  <IconArchive size={16} />
                </button>
              </h6>
              <Form>
                {calendars.map((cal) => {
                  const hasTypes = (typesByCalendar[cal.id] || []).length > 0;
                  const isExpanded = expandedCalendars.has(cal.id);
                  return (
                    <div key={cal.id}>
                      <div className="calendar-item">
                        <Form.Check
                          type="checkbox"
                          id={`calendar-${cal.id}`}
                          checked={selectedCalendars.includes(cal.id)}
                          onChange={() => handleCheckboxToggle(cal.id)}
                          className="calendar-checkbox"
                        />
                        <div
                          className="calendar-color-indicator"
                          style={{ backgroundColor: calendarColors[cal.id] || "#ccc" }}
                        />
                        <label htmlFor={`calendar-${cal.id}`} className="calendar-label">
                          {cal.name}
                          {cal.is_shared && (
                            <span className="shared-indicator">compartilhado</span>
                          )}
                        </label>
                        {canUpdate && (
                          <button
                            type="button"
                            className="calendar-edit-btn"
                            onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleEditCalendar(cal); }}
                            title="Editar calendário"
                          >
                            <IconEdit size={14} />
                          </button>
                        )}
                        {hasTypes && (
                          <button
                            type="button"
                            className="cal-expand-btn"
                            onClick={() => toggleCalendarTypes(cal.id)}
                            title={isExpanded ? 'Ocultar tipos' : 'Ver tipos de atendimento'}
                          >
                            <span className="cal-type-badge">{typesByCalendar[cal.id].length}</span>
                            <IconChevronDown
                              size={12}
                              className={`cal-chevron${isExpanded ? ' cal-chevron--open' : ''}`}
                            />
                          </button>
                        )}
                      </div>
                      <CalendarTypesAccordion calId={cal.id} />
                    </div>
                  );
                })}
              </Form>
            </div>
          </Col>
        )}

        <Col md={isMobile || isTablet ? 12 : 9} className="calendar-main">
          {loading && (
            <div className="loading-overlay">
              <MoonLoader color="var(--primary)" size={40} />
            </div>
          )}
          <FullCalendar
            ref={(ref) => setFullCalendarRef(ref?.getApi())}
            plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
            headerToolbar={
              isMobile
                ? {
                    left: "prev,next",
                    center: "title",
                    right:
                      user?.superuser_level === 0
                        ? "timeGridWeek,timeGridDay"
                        : "dayGridMonth,timeGridWeek,timeGridDay",
                  }
                : isTablet
                ? {
                    left: "prev,next today",
                    center: "title",
                    right:
                      user?.superuser_level === 0
                        ? "timeGridWeek,timeGridDay"
                        : "dayGridMonth,timeGridWeek,timeGridDay",
                  }
                : {
                    left: "prev,next today",
                    center: "title",
                    right: "dayGridMonth,timeGridWeek,timeGridDay",
                  }
            }
            dayHeaderFormat={
              isMobile
                ? {
                    weekday: "narrow",
                  }
                : undefined
            }
            dayHeaderContent={
              isMobile || isTablet ? renderDayHeader : undefined
            }
            titleFormat={
              isMobile
                ? {
                    year: "numeric",
                    month: "long",
                  }
                : undefined
            }
            initialView={
              user?.superuser_level === 0
                ? "timeGridWeek" // Level 0 sempre inicia na semana
                : isMobile
                ? "dayGridMonth" // Mobile normal inicia no mês
                : isTablet
                ? "timeGridWeek" // Tablet normal inicia na semana
                : "timeGridWeek" // Desktop normal inicia na semana
            }
            hiddenDays={calendarSettings.hiddenDays}
            // businessHours={calendarSettings.businessHours}
            editable={canUpdate}
            selectable={canCreate && !isMobile && !isTablet}
            selectMirror={true}
            events={allEventsWithSlots}
            eventClick={handleEventClick}
            eventDrop={handleEventDrop}
            dateClick={handleDateClick}
            select={handleDateSelect}
            locale={ptBrLocale}
            selectAllow={() => canCreate && !isMobile && !isTablet}
            unselectAuto={false}
            selectOverlap={false}
            selectConstraint={null}
            longPressDelay={1000}
            eventLongPressDelay={1000}
            selectLongPressDelay={1000}
            dayMaxEvents={isMobile ? 3 : false}
            eventDisplay="block"
            eventOrder="start,-duration,allDay,title"
            moreLinkClick="popover"
            eventTimeFormat={{
              hour: "2-digit",
              minute: "2-digit",
              hour12: false,
            }}
            slotMinTime={calendarSettings.startTime}
            slotMaxTime={calendarSettings.endTime}
            slotDuration={calculateSlotDuration(settings)}
            slotLabelFormat={{
              hour: "2-digit",
              minute: "2-digit",
              hour12: false,
            }}
            nowIndicator={true}
            slotMinHeight={isMobile ? 48 : 60}
            expandRows={true}
            height={isMobile ? "calc(100dvh - var(--header-height, 70px) - 40px)" : isTablet ? "100vh" : "auto"}
            aspectRatio={isMobile || isTablet ? undefined : 1.3}
            stickyHeaderDates={true}
            buttonText={{
              month: "Mês",
              week: "Semana",
              day: "Dia",
              today: "Hoje",
            }}
            fixedWeekCount={false}
            showNonCurrentDates={true}
            moreLinkText={(num) => `+${num} mais`}
            viewDidMount={() => {
              if (isMobile || isTablet) {
                triggerHapticFeedback("light");
              }
            }}
            datesSet={handleDateRangeChange}
            eventDidMount={(info) => {
              const calendarColor = info.event.extendedProps?.calendarColor;
              const eventColor = getEventColor(
                info.event._def.extendedProps,
                calendarColor
              );

              // Apply calendar/appointment type color
              if (eventColor && !info.event.extendedProps.isFreeSlot) {
                info.el.style.backgroundColor = eventColor;
                info.el.style.borderColor = eventColor;
              }

              // Add status icon. "missing" (faltou) é status do evento e
              // tem precedência sobre confirmation — uma vez que o cliente
              // faltou, o estado da confirmação não importa mais.
              if (!info.event.extendedProps.isFreeSlot) {
                const props = info.event.extendedProps || {};
                const iconKey =
                  props.status === "missing"
                    ? "missing"
                    : props.confirmation || "pending";
                const statusIcon = createStatusIcon(iconKey);
                info.el.insertAdjacentHTML("beforeend", statusIcon);
              }

              // Occupied events
              if (info.event.title === "OCUPADO") {
                info.el.classList.add("fc-event-occupied");
              }
            }}
          />
        </Col>
      </Row>

      {/* Mobile/Tablet Bottom Sheet */}
      {isMobile && !isTablet && <MobileFloatingButton />}
      {isMobile && !isTablet && <MobileBottomSheet />}

      {/* Modal para novo/editar evento */}
      <Modal
        show={showEventModal}
        onHide={() => {
          setShowEventModal(false);
          setSelectedInfo(null);
          reset();
          // *** NOVO: Limpar flag quando modal fechar ***
          isLongPressActiveRef.current = false;
        }}
        centered
        size="lg"
      >
        <Form onSubmit={handleSubmit(onSubmitEvent)}>
          <Modal.Header closeButton>
            <Modal.Title>
              {selectedInfo?.eventId ? "Editar Evento" : "Novo Evento"}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {/* Busca de Cliente no topo do modal */}
            <ClientSearch onClientSelected={handleClientSelected} />
            
            {/* Campos de Cliente - sempre visíveis */}
            <ClientSelector
              control={control}
              register={register}
              setValue={setValue}
              watch={watch}
              errors={errors}
              clearErrors={clearErrors}
              onClientSelected={handleClientSelected}
            />
            
            <Row>
              <Form.Group className="col-md-5 col-sm-12 mb-3">
                <Form.Label>Título</Form.Label>
                <Form.Control {...register("title", { required: true })} />
                {errors.title && (
                  <span className="text-danger">Este campo é obrigatório</span>
                )}
              </Form.Group>
              <Form.Group className="col-md-3 col-sm-12 mb-3">
                <Form.Label>Data</Form.Label>
                <Form.Control
                  type="date"
                  {...register("date", { required: true })}
                />
                {errors.date && (
                  <span className="text-danger">Este campo é obrigatório</span>
                )}
              </Form.Group>
              <Form.Group className="col-md-2 col-sm-12 mb-3">
                <Form.Label>Início</Form.Label>
                <Form.Control
                  className="px-2"
                  type="time"
                  {...register("start", { required: true })}
                />
                {errors.start && (
                  <span className="text-danger">Este campo é obrigatório</span>
                )}
              </Form.Group>
              <Form.Group className="col-md-2 col-sm-12 mb-3">
                <Form.Label>Fim</Form.Label>
                <Form.Control
                  className="px-2"
                  type="time"
                  {...register("end", { required: true })}
                />
                {errors.end && (
                  <span className="text-danger">Este campo é obrigatório</span>
                )}
              </Form.Group>
            </Row>

            <Form.Group className="mb-3">
              <Form.Label>Descrição</Form.Label>
              <Form.Control
                {...register("description")}
                as="textarea"
                rows={3}
              />
            </Form.Group>

            <Row>
              <Form.Group className="col-md-6 col-sm-12 mb-3">
                <Form.Label>Agenda</Form.Label>
                <Form.Select {...register("calendar", { required: true })}>
                  <option value="">Selecione uma agenda</option>
                  {calendars.map((cal) => (
                    <option key={cal.id} value={cal.id}>
                      {cal.name + (cal.is_shared ? " (compartilhado)" : "")}
                    </option>
                  ))}
                </Form.Select>
                {errors.calendar && (
                  <span className="text-danger">Selecione uma agenda</span>
                )}
              </Form.Group>

              <Form.Group className="col-md-6 col-sm-12 mb-3">
                <Form.Label>Tipo de Atendimento</Form.Label>
                <Form.Select {...register("appointment_type")}>
                  <option value="">Selecione um tipo (opcional)</option>
                  {filteredAppointmentTypes.map((type) => (
                    <option key={type.id} value={type.id}>
                      {type.name}{" "}
                      {type.price && ` ${formatCurrency(type.price)}`}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Row>

            <Row>
              <Form.Group className="col-md-6 col-sm-12 mb-3">
                <Form.Label>Preço (R$)</Form.Label>
                <Controller
                  name="price"
                  control={control}
                  render={({ field }) => (
                    <NumberFormat
                      thousandSeparator="."
                      decimalSeparator=","
                      decimalScale={2}
                      fixedDecimalScale={true}
                      allowNegative={false}
                      className="form-control"
                      placeholder="0,00"
                      onValueChange={(values) => {
                        field.onChange(values.floatValue);
                      }}
                      value={field.value || ""}
                    />
                  )}
                />
              </Form.Group>

              <Form.Group className="col-md-6 col-sm-12 mb-3">
                <Form.Label>Confirmação</Form.Label>
                <Form.Select
                  {...register("confirmation")}
                  defaultValue="pending"
                >
                  <option value="pending">Pendente</option>
                  <option value="confirmed">Confirmado</option>
                  <option value="not_confirmed">Não confirmou</option>
                </Form.Select>
              </Form.Group>
            </Row>
            <Row>
              {/* Campo "Tags" comentado temporariamente
              <Form.Group className="col-md-6 col-sm-12 mb-3">
                <TagsSelector
                  control={control}
                  name="tags"
                  label="Tags"
                  placeholder="opcional..."
                  category="client"
                  maxTags={3}
                  isRequired={false}
                  helperText="Adicione tags para categorizar este cliente"
                />
              </Form.Group>
              */}

              <Form.Group className="col-md-6 col-sm-12 mb-3">
                <Form.Label>Status</Form.Label>
                <Form.Select {...register("status")} defaultValue="scheduled">
                  <option value="scheduled">Agendado</option>
                  <option value="canceled">Cancelado</option>
                  <option value="missing">Faltou</option>
                  <option value="rescheduled">Reagendado</option>
                </Form.Select>
              </Form.Group>
            </Row>

            <Row className="mb-3 ml-4">
              <Col>
                <Form.Check
                  type="switch"
                  id="send-automations-switch"
                  label="Enviar automações"
                  {...register("send_automations")}
                  defaultChecked={true}
                />
              </Col>
              <Col>
                <Form.Check
                  type="switch"
                  id="disable-ai-switch"
                  label="Desativar IA"
                  {...register("disable_ai")}
                  defaultChecked={true}
                />
              </Col>
            </Row>

          </Modal.Body>
          <Modal.Footer>
            <Button
              variant="secondary"
              onClick={() => {
                setShowEventModal(false);
                setSelectedInfo(null);
                reset();
                // *** NOVO: Limpar flag ***
                isLongPressActiveRef.current = false;
              }}
            >
              Cancelar
            </Button>
            <Button type="submit" variant="primary" disabled={submitLoading}>
              {submitLoading ? (
                <>
                  <MoonLoader color="#ffffff" size={16} className="me-2" />
                  {selectedInfo?.eventId ? "Salvando..." : "Criando..."}
                </>
              ) : (
                selectedInfo?.eventId ? "Salvar alterações" : "Criar"
              )}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Modal para novo calendário */}
      <Modal
        show={showCalendarModal}
        onHide={() => setShowCalendarModal(false)}
        centered
      >
        <Form onSubmit={handleSubmitCalendar(onSubmitCalendar)}>
          <Modal.Header closeButton>
            <Modal.Title>Novo Calendário</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label>Nome do Calendário</Form.Label>
              <Form.Control {...registerCalendar("name", { required: true })} />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Check
                type="checkbox"
                label="Compartilhado com a conta"
                {...registerCalendar("is_shared")}
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button
              variant="secondary"
              onClick={() => setShowCalendarModal(false)}
            >
              Cancelar
            </Button>
            <Button type="submit" variant="primary">
              Criar Calendário
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Modal para editar calendário */}
      <Modal
        show={showEditCalendarModal}
        onHide={() => {
          setShowEditCalendarModal(false);
          setEditingCalendar(null);
          resetEditCalendar();
        }}
        centered
      >
        <Form onSubmit={handleSubmitEditCalendar(onSubmitEditCalendar)}>
          <Modal.Header closeButton>
            <Modal.Title>Editar Agenda</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label>Nome do Calendário</Form.Label>
              <Form.Control
                {...registerEditCalendar("name", { required: true })}
                placeholder="Digite o nome do calendário"
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Check
                type="checkbox"
                label="Compartilhado com a conta"
                {...registerEditCalendar("is_shared")}
              />
              <Form.Text className="text-muted">
                Quando marcado, este calendário será visível para todos os usuários da conta
              </Form.Text>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Automações no agendamento manual</Form.Label>
              <Form.Text className="d-block text-muted mb-2">
                Selecione quais lembretes do agente serão criados quando você
                fizer um agendamento manual nesta agenda.
              </Form.Text>
              {loadingManualReminders ? (
                <div className="text-muted" style={{ fontSize: "0.85rem" }}>
                  Carregando automações…
                </div>
              ) : manualReminderOptions.length === 0 ? (
                <div className="text-muted" style={{ fontSize: "0.85rem" }}>
                  Nenhuma automação de agendamento está vinculada a esta agenda.
                </div>
              ) : (
                <div
                  style={{
                    maxHeight: "220px",
                    overflowY: "auto",
                    border: "1px solid #e5e7eb",
                    borderRadius: "8px",
                    padding: "8px",
                  }}
                >
                  {manualReminderOptions.map((option) => (
                    <div
                      key={option.key}
                      className="d-flex align-items-start mb-2"
                      style={{ gap: "8px" }}
                    >
                      <Form.Check
                        type="checkbox"
                        id={`reminder-${option.key}`}
                        checked={selectedReminderKeys.includes(option.key)}
                        onChange={() => toggleReminderKey(option.key)}
                      />
                      <label
                        htmlFor={`reminder-${option.key}`}
                        style={{ cursor: "pointer", fontSize: "0.85rem" }}
                      >
                        <div style={{ fontWeight: 500 }}>
                          {formatReminderTrigger(option)}
                          <span className="text-muted">
                            {" "}· {option.channel === "email" ? "E-mail" : "WhatsApp"}
                          </span>
                        </div>
                        <div className="text-muted text-truncate" style={{ maxWidth: "360px" }}>
                          {option.message}
                        </div>
                      </label>
                    </div>
                  ))}
                </div>
              )}
            </Form.Group>

            {canDelete && (
              <div className="mt-4 pt-3" style={{ borderTop: "1px solid #e5e7eb" }}>
                <button
                  type="button"
                  onClick={handleDeleteCalendar}
                  style={{
                    background: "transparent",
                    border: "none",
                    color: "#dc2626",
                    fontSize: "0.8rem",
                    textDecoration: "underline",
                    cursor: "pointer",
                    padding: 0,
                  }}
                >
                  Excluir agenda definitivamente
                </button>
                <Form.Text className="d-block text-muted" style={{ fontSize: "0.72rem" }}>
                  Use arquivar se quiser preservar o histórico — esta ação apaga tudo.
                </Form.Text>
              </div>
            )}
          </Modal.Body>
          <Modal.Footer>
            {canDelete && (
            <Button
              variant="outline-warning"
              onClick={() => {
                setArchivingCalendar(editingCalendar);
                setShowEditCalendarModal(false);
                setShowArchiveModal(true);
              }}
              className="me-auto"
              title="Arquivar mantém o histórico de agendamentos"
            >
              <IconArchive size={16} /> Arquivar
            </Button>
            )}
            <Button
              variant="secondary"
              onClick={() => {
                setShowEditCalendarModal(false);
                setEditingCalendar(null);
                resetEditCalendar();
              }}
            >
              Cancelar
            </Button>
            <Button type="submit" variant="primary">
              Salvar Alterações
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Modal de arquivamento */}
      <ArchiveCalendarModal
        show={showArchiveModal}
        onHide={() => {
          setShowArchiveModal(false);
          setArchivingCalendar(null);
        }}
        calendar={archivingCalendar}
        onArchived={() => {
          fetchCalendars();
          setShowArchiveModal(false);
          setArchivingCalendar(null);
        }}
      />

      {/* Modal de agendas arquivadas */}
      <ArchivedCalendarsModal
        show={showArchivedListModal}
        onHide={() => setShowArchivedListModal(false)}
        canManage={canDelete}
        onChanged={() => fetchCalendars()}
      />

      {/* Modal de detalhes do evento */}
      <EventDetailsModal
        show={showEventDetailsModal}
        onHide={() => {
          setShowEventDetailsModal(false);
          setSelectedEventForDetails(null);
        }}
        event={selectedEventForDetails}
        onEdit={handleEditFromModal}
        onDelete={handleDeleteFromModal}
        canUpdate={canUpdate}
        canDelete={canDelete}
        calendars={calendars}
        appointmentTypes={appointmentTypes}
      />

      {/* Modal de agendamentos do dia (month view mobile) */}
      <DayEventsModal
        show={showDayEventsModal}
        onHide={() => {
          setShowDayEventsModal(false);
          setSelectedDayForEvents(null);
        }}
        date={selectedDayForEvents}
        events={events}
        onEventClick={handleDayEventClick}
        onNewEvent={(dateStr) => {
          setShowDayEventsModal(false);
          setSelectedDayForEvents(null);
          openEventModal(dateStr);
        }}
        canCreate={canCreate}
        calendars={calendars}
      />
    </div>
  );
};

export default Calendar;
